<i18n>
{
  "cl": {
    "simulationData": "Datos de simulación",
    "dataVerification": "Comprobación de datos",
    "logs": "Logs",
    "verify": "Comprobar",
    "floidReport": "Reporte Floid",
    "debtReport": "Reporte de deuda",
    "otherReport": "SBIF - GESINTEL - Poder Judicial",
    "notDefined": "No definido",
    "reviewable": "Revisable",
    "refused": "Rechazado por elegibilidad",
    "approved": "Aprobado"
  }
}
</i18n>
<template>
  <div id="evaluation-panel" class="pt-5 pb-5 is-max-desktop">
    <EvaluationBreadcrumb :links="links" />
    <div class="box">
      <header class="summary-header">
        <article id="summary-client">
          <b-tag
            v-if="evaluation && evaluation.applicationDate !== '' && evaluation.applicationDate !== null && evaluation.applicationDate !== '0000-00-00'"
            type="is-primary is-light mr-4"
            icon="calendar-days"
            data-cy="aplicationDate"
            rounded>
            {{ formatDate(evaluation.applicationDate) }}
          </b-tag>
          <b-tag
            v-if="evaluation && evaluation.simulationOrigin !== '' && evaluation.simulationOrigin !== null"
            type="is-primary is-light mr-4"
            icon="location-arrow"
            data-cy="simulationOrigin"
            rounded>
            {{ evaluation.simulationOrigin }}
          </b-tag>
          <b-tag
            v-if="evaluation && evaluation.type !== '' && evaluation.type !== null"
            type="is-primary is-light mr-4"
            :icon="evaluation.type === 'automatic' ? 'robot' : 'circle-user'"
            data-cy="automatic"
            rounded>
            {{ evaluation.type === 'automatic' ? 'Automática' : 'Manual' }}
          </b-tag>
          <b-tag
            v-if="evaluation && evaluation.future !== '' && evaluation.future !== null"
            type="is-primary is-light mr-4"
            :icon="evaluation.future ? 'hourglass-start' : 'bolt'"
            data-cy="future"
            rounded>
            {{ evaluation.future ? 'Futura' : 'Inmediata' }}
          </b-tag>
        </article>
      </header>
    </div>
    <Spinner v-if="isLoadingEvaluation && !isUnauthorizedError" />
    <div v-else-if="evaluation && !isUnauthorizedError">
      <EvaluationHeader :debtor="debtor" />
      <b-button
        id="btn-home"
        icon-left="home"
        type="is-primary is-light is-rounded"
        @click="goHome" />
      <b-steps
        id="evaluation-steps"
        v-model="activeStep"
        :has-navigation="hasNavigation"
        label-position="right"
        animated
        rounded>
        <b-step-item label="Simulación" :step="0" :clickable="false" :type="stepProperties(0).type" :icon="stepProperties(0).icon">
          <Simulation />
        </b-step-item>
        <b-step-item label="Elegibilidad" :step="1" :clickable="false" :type="stepProperties(1).type" :icon="stepProperties(1).icon">
          <EligibilityCriteria />
        </b-step-item>
        <b-step-item label="Renta" :step="2" :clickable="false" :type="stepProperties(2).type" :icon="stepProperties(2).icon">
          <Income
            label="Renta"
            :evaluation-id="generatedEvaluationId"
            :one-client-id="evaluation.oneClientId"
            @new-income-created="newIncomeCreated"
            @income-documents-uploaded="incomeHasDocuments" />
        </b-step-item>
        <b-step-item label="Deuda" :step="3" :clickable="false" :type="stepProperties(3).type" :icon="stepProperties(3).icon">
          <Debt
            label="Deuda"
            :evaluation-id="generatedEvaluationId"
            :one-client-id="evaluation.oneClientId"
            @new-debt-created="newDebtCreated"
            @new-debt-empty="onNewDebtEmpty"
            @debt-documents-uploaded="debtHasDocuments" />
        </b-step-item>
        <b-step-item label="Resumen" :step="4" :clickable="false" :type="stepProperties(4).type" :icon="stepProperties(4).icon">
          <Resume :evaluation="evaluation" />
        </b-step-item>
        <div :class="buttonClass(activeStep)">
          <b-button
            v-if="activeStep !== 0"
            outlined
            class="is-secondary"
            type="is"
            icon-pack="fas"
            icon-left="chevron-left"
            data-cy="previousStep"
            @click.prevent="handlePreviousStep">
            Anterior
          </b-button>
          <b-button
            v-if="!isLastStep"
            :disabled="!nextButtonEnable"
            type="is-danger"
            icon-pack="fas"
            icon-right="chevron-right"
            data-cy="nextStep"
            @click.prevent="handleNextStep">
            Siguiente
          </b-button>
          <b-button
            v-if="isReadyForRatify()"
            type="is-danger"
            icon-pack="fas"
            icon-right="chevron-right"
            data-cy="ratifyButton"
            @click.prevent="enableShowConfirmation">
            Ratificar
          </b-button>
          <div
            v-if="showConfirmation"
            data-cy="confirmation-dialog"
            class="is-flex-desktop is-align-items-center">
            <p class="is-pulled-left mr-4">¿Seguro quieres ratificar la verificación?</p>
            <b-button type="is-danger" data-cy="button-confirm" @click="ratify">Sí</b-button>
            <b-button type="is-text" data-cy="button-cancel" @click="cancelRatification">
              No
            </b-button>
          </div>
        </div>
      </b-steps>
    </div>
    <b-notification
      v-if="isUnauthorizedError"
      class="mb-54"
      style="border-radius: 10px"
      type="is-warning is-light"
      has-icon
      aria-close-label="Close notification"
      role="alert">
      No cuentas con permisos para gestionar este proceso.
    </b-notification>
  </div>
</template>

<script>
import Spinner from '@/ui/components/Spinner.vue';
import EvaluationHeader from '@/ui/components/internal/evaluations/EvaluationHeader.vue';
import Income from '@/ui/components/internal/evaluations/verify/Income.vue';
import Debt from '@/ui/components/internal/evaluations/verify/Debt.vue';
import Simulation from '@/ui/components/internal/evaluations/verify/Simulation.vue';
import Resume from '@/ui/components/internal/evaluations/verify/Resume.vue';
import EvaluationBreadcrumb from '@/ui/components/internal/evaluations/EvaluationBreadcrumb.vue';
import { mapMutations, mapState } from 'vuex';
import '@/ui/assets/css/custom-components.css';
import { EvaluationService } from '@/application/services/evaluations/evaluation-service';
import { BreadcrumbService } from '@/application/services/evaluations/breadcrumb-service';
import {
  EvaluationParamsCreator
} from '@/application/services/evaluations/evaluation-params-creator';
import { EvaluationRetriever } from '@/application/services/evaluations/evaluation-retriever';
import { EvaluationSaver } from '@/application/services/evaluations/evaluation-saver';
import { EvaluationRatifier } from '@/application/services/evaluations/evaluation-ratifier';
import { BffEvaluationsClient } from '@/application/services/evaluations/apis/bff-evaluations-client';
import {
  EvaluationDataProcessor
} from '@/application/services/evaluations/evaluation-data-processor';
import { EvaluationsClient } from '@/application/services/evaluations/apis/evaluations-client';
import { evaluationsBackend } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';
import EligibilityCriteria
  from '@/ui/components/internal/evaluations/verify/EligibilityCriteria.vue';
import { ShortNameService } from '@/application/services/evaluations/short-name-service';
import { objectId } from '@/application/services/utils/object-id';
import { formatDate } from '@/infrastructure/format';

export default {
  name: 'EvaluationProcess',
  components: {
    EvaluationBreadcrumb,
    EvaluationHeader,
    Spinner,
    Income,
    EligibilityCriteria,
    Debt,
    Simulation,
    Resume
  },
  data() {
    return {
      evaluation: null,
      evaluationId: null,
      generatedEvaluationId: objectId(),
      isLoadingEvaluation: true,
      activeStep: 0,
      hasNavigation: false,
      links: [],
      showConfirmation: false,
      hasCompletedAllSteps: false,
      evaluationService: new EvaluationService(
        this.$store,
        this.$router,
        new EvaluationParamsCreator(),
        new EvaluationRetriever(new BffEvaluationsClient(evaluationsBackend, authenticationService)),
        new EvaluationSaver(new EvaluationsClient(evaluationsBackend, authenticationService)),
        new EvaluationRatifier(this.$router, new BffEvaluationsClient(evaluationsBackend, authenticationService), this.$buefy),
        new EvaluationDataProcessor(this.$store)
      ),
      breadcrumbService: new BreadcrumbService(),
      nextButtonEnable: true,
      debtValidate: {
        hasNewDebt: false,
        hasDocuments: false
      },
      incomeValidate: {
        hasNewIncome: false,
        hasDocuments: false
      },
      isUnauthorizedError: false
    };
  },
  computed: {
    ...mapState('evaluation', [
      'debt',
      'debtor',
      'eligibilityResult',
      'idNumber',
      'income',
      'manualEvaluationId',
      'parentEvaluationId',
      'simulation',
      'userId'
    ]),
    isLastStep() {
      return this.activeStep === 4;
    }
  },
  async mounted() {
    const parentEvaluationId = this.$route.params.evaluationId;
    await this.loadEvaluationsParams(parentEvaluationId);
  },
  methods: {
    ...mapMutations('evaluation', ['setManualEvaluationId', 'setEvaluationParams', 'resetState']),
    formatDate,
    async loadEvaluationsParams(parentEvaluationId, isSummary = false) {
      await this.resetState();
      let evaluationsParams = {};

      try {
        this.evaluation = await this.evaluationService.getEvaluation(parentEvaluationId);
        if (!this.evaluation) {
          throw new Error('No se pudo obtener la evaluación');
        }
        const { idNumber, type: evaluationType } = this.evaluation;

        const userId = this.$getSafe(this.$store, 'state.currentUser.user._id', undefined);
        evaluationsParams = { userId, parentEvaluationId, idNumber };
        const evaluationClient = new BffEvaluationsClient(evaluationsBackend, authenticationService);

        const response = await evaluationClient.getEvaluationsParamsByIdNumber(idNumber);
        const { evaluationId, type, initialData = {} } = response.data;

        evaluationsParams = { ...evaluationsParams, ...initialData, ...{ evaluationType: type } };
        await this.evaluationService.getSimulation(evaluationId, type);
        await this.evaluationService.getDebtor(idNumber);
        await this.setManualEvaluationId(evaluationId);
      } catch (error) {
        this.isLoadingEvaluation = false;
        if (error.response && error.response.status === 401) {
          this.isUnauthorizedError = true;
        }
        this.$log.error('Error fetching evaluations params:', error);
      } finally {
        await this.setEvaluationParams(evaluationsParams);
        this.setLinksForBreadcrumb();
        this.isLoadingEvaluation = false;
        if (!isSummary) {
          this.activeStep = 0;
        }
      }
    },
    stepProperties(id) {
      const isActiveStep = this.activeStep >= id;
      return {
        type: isActiveStep ? 'is-primary' : '',
        icon: isActiveStep ? 'check' : ''
      };
    },
    async handleNextStep() {
      this.activeStep += 1;
      if (this.isLastStep) {
        this.isLoadingEvaluation = true;
        await this.saveManualEvaluation();
        await this.loadEvaluationsParams(this.parentEvaluationId, true);
        this.evaluation = await this.evaluationService.getEvaluation(this.manualEvaluationId);
        this.isLoadingEvaluation = false;
      }
    },
    enableShowConfirmation() {
      this.showConfirmation = true;
    },
    handlePreviousStep() {
      this.activeStep = this.activeStep > 0 ? this.activeStep - 1 : this.activeStep;
      this.showConfirmation = false;
    },
    goHome() {
      const { evaluationId } = this.$route.params;
      this.$router.push({
        name: 'evaluation-detail',
        params: { evaluationId }
      });
    },
    setLinksForBreadcrumb() {
      this.links = this.breadcrumbService.getLinks(
        this.$route.params.evaluationId,
        this.$getSafe(this, 'debtor.fullName', ''),
        'evaluation-process'
      );
    },
    buttonClass(activeStep) {
      const defaultClass = 'steps-navigation';
      return activeStep === 0 ? `${defaultClass} is-justify-content-flex-end` : defaultClass;
    },
    cancelRatification() {
      this.showConfirmation = false;
    },
    isReadyForRatify() {
      const approvedCreditAmount = this.$getSafe(this.evaluation, 'approvedCreditAmount', null);
      return !!(!this.showConfirmation && this.manualEvaluationId && this.isLastStep && approvedCreditAmount && approvedCreditAmount > 0);
    },
    async saveManualEvaluation() {
      const serviceShortName = new ShortNameService();
      const userShortName = await serviceShortName.getShortName(this.userId);
      await this.evaluationService.saveManualEvaluation({
        evaluationId: this.generatedEvaluationId,
        idNumber: this.idNumber,
        userId: this.userId,
        userShortName,
        parentEvaluationId: this.parentEvaluationId,
        simulation: this.simulation,
        income: this.income,
        debt: this.debt,
        eligibility: this.eligibilityResult
      });
    },
    async ratify() {
      try {
        this.isLoadingEvaluation = true;
        const { evaluationId } = this.$route.params;
        const { manualEvaluationId } = this;
        await this.evaluationService.ratify(manualEvaluationId, evaluationId);
      } catch (e) {
        this.$log.error('Error ratifying evaluation:', e.message);
      } finally {
        this.isLoadingEvaluation = false;
      }
    },
    isNextButtonEnabled() {
      switch (this.activeStep) {
        case 2: // Income
          this.nextButtonEnable = !(this.incomeValidate.hasNewIncome && !this.incomeValidate.hasDocuments);
          break;
        case 3: // Debt
          this.nextButtonEnable = !(this.debtValidate.hasNewDebt && !this.debtValidate.hasDocuments);
          break;
        default: // Otros stes
          this.nextButtonEnable = true;
          break;
      }
    },
    newDebtCreated(hasNewDebt) {
      this.debtValidate.hasNewDebt = hasNewDebt;
      this.isNextButtonEnabled();
    },
    onNewDebtEmpty() {
      this.debtValidate.hasNewDebt = false;
      this.isNextButtonEnabled();
    },
    debtHasDocuments(hasDocuments) {
      this.debtValidate.hasDocuments = hasDocuments;
      this.isNextButtonEnabled();
    },
    newIncomeCreated(hasNewIncome) {
      this.incomeValidate.hasNewIncome = hasNewIncome;
      this.isNextButtonEnabled();
    },
    incomeHasDocuments(hasDocuments) {
      this.incomeValidate.hasDocuments = hasDocuments;
      this.isNextButtonEnabled();
    }
  }
};
</script>

<style>
#evaluation-panel {
  color: #4D4D4D;
}

#evaluation-panel header {
  color: #828282;
}

.tag:not(body).is-primary.is-light {
  background-color: rgba(0, 173, 168, 0.11);
  color: var(--primary);
}

#evaluation-steps {
  width: 100%;
  -webkit-box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.02);
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.02);
  border-radius: 10px;
  margin-top: -36px;
}

#btn-home.is-primary.is-light {
  background-color: rgba(0, 173, 168, 0.11);
  border-radius: 100%;
  padding: 0.3em 1em 0.2em;
}

#btn-home.is-primary.is-light:hover,
#btn-home.is-primary.is-light:focus {
  background-color: rgba(0, 173, 168, 0.8);
  color: #FFF;
}
#evaluation-panel .box{
  padding: 10px 25px;
  border-radius: 10px;
}
@media screen and (max-width: 991px) {
  #evaluation-steps .steps.has-label-right .step-items .step-item .step-link > .step-details,
  #evaluation-steps .steps.has-label-left .step-items .step-item .step-link > .step-details {
    background-color: #F9F9F9;
  }

  #btn-home {
    text-align: center;
    margin: -15px auto 50px auto;
    display: block;
  }
}

@media screen and (max-width: 767px){
  #evaluation-panel .box .tag:not(body).is-primary.is-light{
    margin-bottom: 0.5rem;
  }
}
#evaluation-steps .steps .step-items .step-item .step-details .step-title {
  font-weight: 400;
  font-size: 0.8rem;
}

#evaluation-steps .steps .step-items .step-item.is-active .step-details .step-title {
  color: var(--primary);
}

#evaluation-steps .steps {
  border-radius: 10px 10px 0 0;
  background-color: #F9F9F9;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: block;
  padding: 1rem 0;
}

#evaluation-steps section.step-content {
  border-radius: 0 0 10px 10px;
  padding: 2rem 2.5rem;
  background-color: #FFF;
}

#evaluation-steps input[type=text],
#evaluation-steps input[type=number],
#evaluation-steps textarea,
#evaluation-steps select {
  background-color: #FFFFFF !important;
  border-color: #ECEFF4;
}

#evaluation-steps .field .has-addons a {
  height: 30px;
  background-color: #EEF2F6;
  border-color: #ECEFF4;
}

#evaluation-steps .switch .check {
  background-color: #CDD5DF;
}

#evaluation-steps .switch .check:before {
  background-color: #FFFFFF;
}

#evaluation-steps .switch input[type=checkbox]:checked + .check {
  background-color: var(--primary);
}

.steps-navigation {
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
}

.field .has-addons a {
  height: 30px;
}

.field-label label {
  font-size: 0.875rem !important;
  font-weight: 400;
}

@media screen and (min-width: 992px) {
  #btn-home {
    position: relative;
    top: 14px;
    left: 20px;
  }

  #evaluation-steps .steps .step-items {
    width: 90%;
    margin: 0 0 0 auto;
  }

  #evaluation-steps .steps .step-items .step-item:not(:first-child)::before,
  #evaluation-steps .steps .step-items .step-item:only-child::before {
    content: "\f105";
    font-family: 'Font Awesome 6 Free';
    display: inline-block;
    position: absolute;
    top: 20%;
    left: 0;
    width: 100%;
    transform: translateY(-50%);
    font-weight: 900;
    background: transparent;
  }

  #evaluation-steps .steps.has-label-right .step-items .step-item .step-link > .step-details,
  #evaluation-steps .steps.has-label-left .step-items .step-item .step-link > .step-details {
    padding: 0.2em 0.2em 0.2em 0.5em;
    background-color: transparent;
  }

  #evaluation-steps .steps .step-items .step-item .step-marker {
    border-color: transparent;
  }

  #evaluation-steps .steps .step-items .step-item.is-active .step-marker {
    background-color: rgba(0, 173, 168, 0.11);
  }

  .field-label label {
    text-align: left;
  }
}
</style>
